<style scoped lang="scss">
    .modal-box {
        background-color: #fff;
    }
    form {
        h2 {
            color: #000;
        }
        ::v-deep(.formkit-label), ::v-deep(legend) {
            color: #000 !important;
            font-weight: 400;
        }
    }
</style>
<template>
    <dialog ref="HTMLDialogElement" :id="supplierFormModalId" class="modal modal-middle sm:modal-middle">
        <div class="modal-box" method="dialog">
            <FormKit
                type="form"
                id="supplierForm"
                @submit="submitHandler"
                :plugins="[zodPlugin]"
                :actions="false"
                incomplete-message="Désolé, tous les champs ne sont pas remplis correctement."
                #default="{ state: { valid } }"
            >
                <h2>{{ modalTitle }}</h2>
                <hr />
                <FormKit
                    type="radio"
                    v-model="isFee"
                    label="Frais de gestion :"
                    name="isFee"
                    :options="[
                        { label: 'Oui', value: '1' },
                        { label: 'Non', value: '0' },
                    ]"
                    @select="triggerFee()"
                />
                <FormKit
                    type="number"
                    v-show="showMontant"
                    v-model="supplier.fees"
                    name="fees"
                    label="Montant"
                    step="any"
                    help="un nombre entier ou si décimal, il faut 1 à 3 chiffres avant le (.) ou la (,)
                        et 1 à 2 chiffres après le (.) ou la (,)"
                />
                <FormKit
                    v-model="supplier.name"
                    type="text"
                    name="name"
                    label="Nom du fournisseur"
                />
                <div class="grid grid-cols-2 grid-rows-1 gap-6">
                    <FormKit
                        v-model="supplier.email"
                        type="text"
                        name="email"
                        label="Email"
                    />
                    <FormKit
                        type="text"
                        v-model="supplier.phoneNumber"
                        name="phoneNumber"
                        label="Téléphone"
                    />
                </div>
                <FormKit
                    type="textarea"
                    v-model="supplier.notes"
                    name="notes"
                    label="Note"
                />
                <div class="modal-action">
                    <button type="button" class="btn" @click="closeModalDialog()">Annuler</button>
                    <FormKit type="submit" :label="props.supplierUuid ? 'Modifier' : 'Créer'" :disabled="!valid" />
                </div>
            </FormKit>
        </div>
    </dialog>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch, toRef } from "vue";
import {
    createSupplier,
    getSupplier,
    updateSupplier,
} from "@/_services/supplier/supplier";
import { createZodPlugin } from "@formkit/zod";
import { z } from "zod";
import swal from "@/_helpers/swal";
import { ISupplier } from "@/models/supplier/ISupplier";
const isFee = ref("0");
const loaded = ref(false);
const showMontant = ref(true);
const supplier = ref<ISupplier>({
    name: "",
    email: "",
    phoneNumber: "",
    notes: "",
    fees: 0,
} as unknown as ISupplier);
const props = defineProps({
    supplierUuid: {
        type: String,
        required: false,
        default: "",
    },
    supplierFormModalId: {
        type: String,
        required: true,
    },
});
const supplierFormModalId = toRef(props, "supplierFormModalId");
const modalHTMLElement = ref<HTMLDialogElement | null>(null);
const supplierUuid = toRef(props, "supplierUuid");
const modalDialogState = ref<"open" | "close">("close");

const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
        if (mutation.attributeName === "open" && modalHTMLElement.value) {
            const isOpen = modalHTMLElement.value.open;
            const isclosed = !isOpen;
            modalDialogState.value = isOpen ? "open" : "close";
            if (supplierUuid.value && isclosed) {
                updateSupplierFromApi(supplierUuid.value as string);
            }
            if (
                (!supplierUuid.value && isclosed) ||
                (!supplierUuid.value && isOpen)
            ) {
                resetSupplierData();
            }

            break; // Exit the loop once the class attribute has been processed
        }
    }
});

onMounted(() => {
    modalHTMLElement.value = document.querySelector(
        `#${supplierFormModalId.value}`
    );
    if (modalHTMLElement.value) {
        observer.observe(modalHTMLElement.value, { attributes: true });
    }
});

const modalTitle = props.supplierUuid
    ? ref<string>("Modifier un fournisseur")
    : ref<string>("Créer un fournisseur");

const savedFee = ref<number>(0);

const triggerFee = () => {
    if (isFee.value == "0") {
        savedFee.value = supplier.value.fees as number;
        supplier.value.fees = 0;
    } else {
        supplier.value.fees = savedFee.value;
    }
};

const zodSchema = z.object({
    name: z.string()
        .nonempty("Le champs Nom du fournisseur est obligatoire")
        .min(1, { message: "Le champs Nom du fournisseur est obligatoire" })
        .max(50, { message: "Le nom du fournisseur ne doit pas dépasser 50 caractères" }),
    fees: z.coerce
        .number()
        .nullable()
        .refine((data) => validateFee(data), { message: "Le montant est invalide" }),
    notes: z.string()
        .max(255, { message: "La note ne doit pas dépasser 255 caractères" })
        .nullable(),
    email: z.string()
        .max(255, { message: "L'email ne doit pas dépasser 255 caractères" })
        .nullable(),
    phoneNumber: z.string()
        .max(255, { message: "Le numéro de téléphone ne doit pas dépasser 255 caractères" })
        .nullable(),
});

const decimalFeesRegex = new RegExp(/^\d{1,3}(\.\d{1,2})?$/);
const validateFee = (data) => {
    return !(
        isFee.value == "1" &&
        supplier.value.fees != null &&
        supplier.value.fees >= 0 &&
        false === decimalFeesRegex.test(data as string)
    );
};

const [zodPlugin, submitHandler] = createZodPlugin(
    zodSchema,
    async (formData) => {
        try {
            closeModalDialog();
            const { name, fees, notes, email, phoneNumber } = formData;
            const data = {
                name,
                fees: fees && isFee.value == "1" ? fees.toString() : "0",
                email: typeof email === "string" ? email.trim() : null,
                notes: typeof notes === "string" ? notes : null,
                phoneNumber: null === phoneNumber ? null : phoneNumber.trim()
            };
            const isEdit = Boolean(props.supplierUuid);
            const title = isEdit
                ? "Êtes-vous sûr de vouloir éditer le fournisseur"
                : "Êtes-vous sûr de vouloir créer le fournisseur";
            const confirmText = isEdit ? "Oui, éditer" : "Oui, créer";

            await swal
                .confirm(title, "Annuler", confirmText)
                .then((results) => results.isConfirmed && createOrUpdate(data));
        } catch (err: any) {
            swal.error(err.errors[0]);
        }
    }
)

const createOrUpdate = (data) => {
    props.supplierUuid
        ? updateSupplier(props.supplierUuid as string, data)
        : createSupplier(data);
    closeModalDialog();
};

const closeModalDialog = () => {
    if (modalHTMLElement.value) {
        modalHTMLElement.value.close();
    }
};

const resetSupplierData = () => {
    supplier.value.name = "";
    supplier.value.email = "";
    supplier.value.phoneNumber = "";
    supplier.value.notes = "";
    supplier.value.fees = 0;
    loaded.value = true;
    isFee.value = "0";
};

watch(
    () => props.supplierUuid,
    (uuid) => {
        if (uuid) {
            modalTitle.value = "Modifier un fournisseur";
            updateSupplierFromApi(uuid as string);
        } else {
            modalTitle.value = "Créer un fournisseur";
        }
    }
);
watch(
    isFee,
    (value) => {
        showMontant.value = value == "1";
    },
    { immediate: true }
);

const updateSupplierFromApi = (uuid: string) => {
    getSupplier(uuid).then((res) => {
        supplier.value = res;
        supplier.value.phoneNumber = supplier.value.phoneNumber
            ? supplier.value.phoneNumber
            : "";
        isFee.value =
            supplier.value.fees != null && supplier.value.fees != 0 ? "1" : "0";
        supplier.value.fees = supplier.value.fees != null ? supplier.value.fees : 0;
        loaded.value = true;
    });
};
</script>